import React, { useEffect, useMemo } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import {
  matchPath,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams
} from 'react-router-dom';

import { ErrorFallback } from '@appcharge/shared-ui';
import usePermissions from 'api/usePermissions';
import { useAxiosInterceptor } from 'hooks/useAxiosInterceptor';
import { useLogin } from 'hooks/useLogin';
import LoginPage from 'pages/Login/Login';
import { checkDefaultProjectPage } from 'utils/navigationUtil';
import PrivateRoute from 'utils/privateRoute';
import CreateFirstProjectView from 'views/SelfServe/CreateFirstProject/CreateFirstProjectView';
import SignUpView from 'views/SelfServe/SignUp/SignUpView';
import SubscriptionsView from 'views/Subscriptions/SubscriptionsView';

import { Order } from 'components/Order/Order';
import { Orders } from 'components/Orders/Orders/Orders';

import { EModalState } from '../components/LoginModal/LoginModal.types';
import PasswordTab from '../components/PasswordsTab/PasswordTab';
import PersonalInfo from '../components/PersonalInfo/PersonalInfo';
import { Layout } from '../pages/Layout/Layout';
import ProfileView from '../views/Profile/ProfileView';
import { ProjectsView } from '../views/Projects';
import FinancialReportsTable from '../views/Reports/FinancialReports';
import PayoutsTable from '../views/Reports/Payouts';
import ReportsView from '../views/Reports/ReportsView';

import portalLevelRoutes from './PortalLevelRoutes';
import portalLevelStoreRoutes from './PortalLevelStoreRoutes';
import projectLevelRoutes from './ProjectLevelRoutes';

const AppRoutes = () => {
  const navigate = useNavigate();
  const handleNavigate = () => {
    navigate('/');
  };
  const { publisherId: routeParamPublisherId } = useParams();
  const location = useLocation();
  const { getPermissions } = usePermissions();
  const permissions = getPermissions();
  const { mustInitiateProject } = useLogin();

  const [searchParams] = useSearchParams();

  const matchPublisher = matchPath(
    '/project/:publisherId/*',
    location.pathname
  );
  const publisherId =
    matchPublisher?.params?.publisherId || routeParamPublisherId;

  useAxiosInterceptor();

  useEffect(() => {
    const mode = searchParams.get('mode') as EModalState | null;
    const validModes: EModalState[] = [
      EModalState.LOGIN,
      EModalState.RESET_PASSWORD,
      EModalState.FORGET_PASSWORD,
      EModalState.FINISH_ACCOUNT
    ];
    if (mode && validModes.includes(mode)) {
      navigate(`/login?${searchParams.toString()}`, { replace: true });
    }
  }, [searchParams, navigate]);

  const defaultProjectPage = checkDefaultProjectPage(
    permissions,
    publisherId as string
  );

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/sign-up/*" element={<SignUpView />} />
      <Route path="/init-project/*" element={<CreateFirstProjectView />} />
      <Route
        path="/*"
        element={
          <PrivateRoute>
            <ErrorBoundary
              FallbackComponent={() => (
                <ErrorFallback handleClick={handleNavigate} />
              )}
            >
              <Layout />
            </ErrorBoundary>
          </PrivateRoute>
        }
      >
        {mustInitiateProject ? (
          <>
            {/* Redirect all other paths */}
            <Route path="*" element={<Navigate to="/init-project" replace />} />
          </>
        ) : (
          <>
            <Route index element={<Navigate to="/projects" replace />} />
            <Route path="projects" element={<ProjectsView />} />
            <Route path="welcome" element={<Navigate to="/projects" replace />} />
            <Route path="orders" element={<Orders />} />

            <Route
              path="orders/:orderId"
              element={<Order />}
            />
            <Route
              path="orders/free/:orderId"
              element={<Order />}
            />
            <Route path="reports" element={<ReportsView />}>
              <Route
                path=""
                element={<Navigate to="financialreports" replace />}
              />
              <Route
                path="financialreports"
                element={<FinancialReportsTable />}
              />
              <Route path="payouts" element={<PayoutsTable />} />
            </Route>
            <Route path="subscriptions/*" element={<SubscriptionsView />} />
            <Route path="profile" element={<ProfileView />}>
              <Route path="personal" element={<PersonalInfo />} />
              <Route path="password" element={<PasswordTab />} />
            </Route>
            <React.Fragment key="project-level-routes">
              {projectLevelRoutes(defaultProjectPage)}
            </React.Fragment>
            <React.Fragment key="portal-level-routes">
              {portalLevelRoutes()}
            </React.Fragment>
            <React.Fragment key="portal-level-store-routes">
              {portalLevelStoreRoutes('bundles')}
            </React.Fragment>
          </>
        )}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
};

export default AppRoutes;
